// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import appColors, { alpha } from '@/theme/colors';

import { buildJobLogsUrl } from './jobLogsUrl';
import { cancelBatchQueue, useBatchQueue } from './useBatchQueue';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * One queue, in full, in front of the fleet page.
 *
 * The tile's number answers "how deep"; everything an operator asks next — which course, which
 * job of it, what it is waiting for, how long it has been waiting, which worker took it — is a
 * property of the rows. Those rows come from the queue endpoint rather than the fleet payload's
 * activity feed, which carries only the most recent jobs and left a twenty-four-deep queue
 * showing two of them.
 *
 * Oldest first: that is the order a worker takes them in, so the top row is the one that has
 * been waiting longest and the one to explain first.
 */
export function BatchQueueModal({
  apiBaseUrl,
  token,
  kind,
  label,
  onClose,
  t,
}: {
  apiBaseUrl: string;
  token: string | null;
  /** Null keeps the window closed; the fetch starts when it is set. */
  kind: string | null;
  /** The tile's own label, so the window is titled the way it was opened. */
  label: string;
  onClose: () => void;
  t: Translate;
}) {
  const state = useBatchQueue(apiBaseUrl, token, kind);
  // Clearing a queue throws work away, so it takes two presses: the button says what it is
  // about to do and how much of it, and only the second press does it. Deliberately not
  // Alert.alert — react-native-web does not render one, and the fleet page is used on the web.
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Reopening on another queue must not arrive with the trigger already pulled.
  useEffect(() => {
    setArmed(false);
    setFailed(false);
  }, [kind]);

  const clear = async (jobId?: string) => {
    if (!kind || busy) return;
    setBusy(true);
    const cancelled = await cancelBatchQueue(apiBaseUrl, token, kind, jobId);
    setBusy(false);
    setArmed(false);
    setFailed(cancelled === null);
    if (cancelled !== null) state.reload();
  };

  return (
    <Modal
      visible={kind !== null}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      testID="batch-queue-modal"
    >
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{label}</Text>
            {state.status === 'ready' && state.jobs.length > 0 ? (
              <Pressable
                onPress={() => (armed ? void clear() : setArmed(true))}
                accessibilityRole="button"
                disabled={busy}
                testID="batch-queue-clear"
                style={[styles.clearButton, armed && styles.clearButtonArmed]}
              >
                <Text style={styles.clearText}>
                  {armed
                    ? t('batchFleetPage.queueClearConfirm', { count: state.count })
                    : t('batchFleetPage.queueClear')}
                </Text>
              </Pressable>
            ) : null}
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              testID="batch-queue-close"
              style={styles.closeButton}
            >
              <Text style={styles.closeText}>{t('batchFleetPage.stepsClose')}</Text>
            </Pressable>
          </View>

          {failed ? (
            <Text style={styles.paragraph} testID="batch-queue-cancel-failed">
              {t('batchFleetPage.queueCancelFailed')}
            </Text>
          ) : null}

          {state.status === 'loading' ? (
            <Text style={styles.paragraph}>{t('batchFleetPage.queueLoading')}</Text>
          ) : state.status === 'forbidden' ? (
            <Text style={styles.paragraph} testID="batch-queue-forbidden">
              {t('batchFleetPage.forbidden')}
            </Text>
          ) : state.status === 'failed' ? (
            <Text style={styles.paragraph} testID="batch-queue-failed">
              {t('batchFleetPage.queueFailed')}
            </Text>
          ) : state.jobs.length === 0 ? (
            <Text style={styles.paragraph} testID="batch-queue-empty">
              {t('batchFleetPage.queueEmpty')}
            </Text>
          ) : (
            <>
              <Text style={styles.summary} testID="batch-queue-summary">
                {t('batchFleetPage.queueSummary', { count: state.count })}
              </Text>
              <ScrollView style={styles.scroll}>
                {state.jobs.map((job) => (
                  <View key={job.id} style={styles.row} testID="batch-queue-row">
                    <View style={styles.rowHeader}>
                      <View style={[styles.badge, job.status === 'planned' ? styles.badgeWarn : styles.badgeBusy]}>
                        <Text style={styles.badgeText}>{job.status}</Text>
                      </View>
                      <Text style={styles.rowTitle} numberOfLines={1}>
                        {job.detail ?? label}
                      </Text>
                      <Text style={styles.rowCourse} numberOfLines={1}>
                        {job.summary ?? ''}
                      </Text>
                    </View>
                    <Text style={styles.meta} numberOfLines={1}>
                      {[
                        t('batchFleetPage.queueWaitingFor', { time: sinceLabel(job.requestedAt, t) }),
                        // Why it has not gone out yet, when that is knowable — the acquisition
                        // it waits for is the single most useful thing on a planned row.
                        job.status === 'planned' && job.waitingOn
                          ? t('courseAnalysis.status.jobWaitingOn', { source: job.waitingOn })
                          : null,
                        job.workerId ? t('batchFleetPage.byWorker', { id: job.workerId }) : null,
                        job.attempt > 1 ? t('analysisFlowPage.laneAttempt', { attempt: job.attempt }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    <View style={styles.rowActions}>
                      <Pressable
                        accessibilityRole="link"
                        accessibilityLabel={t('batchFleetPage.jobLogsLink')}
                        testID="batch-queue-row-logs"
                        onPress={() => Linking.openURL(buildJobLogsUrl(job.id))}
                      >
                        <Text style={styles.logs}>{t('courseAnalysis.status.jobLogs')}</Text>
                      </Pressable>
                      {/* One job off the queue. No arming step here: it throws away one thing,
                          it is named on the row, and a mis-click costs a re-request. */}
                      <Pressable
                        accessibilityRole="button"
                        disabled={busy}
                        testID="batch-queue-row-cancel"
                        onPress={() => void clear(job.id)}
                      >
                        <Text style={styles.cancel}>{t('batchFleetPage.queueCancelJob')}</Text>
                      </Pressable>
                    </View>
                  </View>
                ))}
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

/** How long this job has been in the queue, in the coarsest unit that still says something. */
const sinceLabel = (iso: string, t: Translate): string => {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - parsed) / 1000));
  if (seconds < 60) return t('batchFleetPage.secondsAgo', { count: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('batchFleetPage.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('batchFleetPage.hoursAgo', { count: hours });
  return t('batchFleetPage.daysAgo', { count: Math.round(hours / 24) });
};

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: alpha(appColors.neutral.black, 0.5), justifyContent: 'center', padding: 16 },
  sheet: {
    maxHeight: '85%',
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 16,
    gap: 8,
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  title: { color: appColors.text.primary, fontSize: 18, fontWeight: '700', flexShrink: 1 },
  closeButton: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  closeText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 13 },
  clearButton: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.status.danger,
    marginLeft: 'auto',
  },
  clearButtonArmed: { backgroundColor: alpha(appColors.status.danger, 0.25) },
  clearText: { color: appColors.status.danger, fontWeight: '700', fontSize: 13 },
  paragraph: { color: appColors.text.secondary, fontSize: 13 },
  summary: { color: appColors.text.muted, fontSize: 12 },
  scroll: { flexGrow: 0 },
  row: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: appColors.border.default,
    paddingVertical: 8,
    gap: 2,
  },
  rowHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  badgeWarn: { backgroundColor: alpha(appColors.status.warning, 0.35) },
  badgeBusy: { backgroundColor: alpha(appColors.accent.light, 0.25) },
  badgeText: { color: appColors.text.primary, fontSize: 11, fontWeight: '700' },
  rowTitle: { color: appColors.text.primary, fontSize: 13, flexShrink: 1 },
  rowCourse: { color: appColors.text.secondary, fontSize: 13, flexShrink: 1 },
  meta: { color: appColors.text.muted, fontSize: 12 },
  rowActions: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  logs: { color: appColors.accent.primary, fontSize: 12, textDecorationLine: 'underline' },
  cancel: { color: appColors.status.danger, fontSize: 12, textDecorationLine: 'underline' },
});
